/**
 * Renderiza QUALQUER um dos 10 modelos a partir de tokens + dados do cliente.
 * Sem hooks: funciona no servidor (SEO) e no preview do editor.
 */
import { MARCAS, iconeDoLink } from './marcas';

const GoogleG = ({ size = 18 }) => (
  <span style={{
    width: size + 4, height: size + 4, borderRadius: '50%', background: '#fff',
    display: 'grid', placeItems: 'center', flex: 'none',
  }}>
    <svg viewBox="0 0 48 48" style={{ width: size - 4, height: size - 4, display: 'block' }} aria-hidden="true">
      <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h11.8c-.5 2.8-2 5.1-4.4 6.6v5.5h7.1c4.2-3.8 6.6-9.5 6.6-16.1z" />
      <path fill="#34A853" d="M24 46c5.9 0 10.9-2 14.6-5.3l-7.1-5.5c-2 1.3-4.5 2.1-7.5 2.1-5.7 0-10.6-3.9-12.3-9.1H4.3v5.7C8 41.1 15.4 46 24 46z" />
      <path fill="#FBBC05" d="M11.7 28.2c-.4-1.3-.7-2.7-.7-4.2s.3-2.9.7-4.2v-5.7H4.3C2.9 17.1 2 20.5 2 24s.9 6.9 2.3 9.9l7.4-5.7z" />
      <path fill="#EA4335" d="M24 10.8c3.2 0 6.1 1.1 8.4 3.3l6.3-6.3C34.9 4.2 29.9 2 24 2 15.4 2 8 6.9 4.3 14.1l7.4 5.7c1.7-5.2 6.6-9.1 12.3-9.1z" />
    </svg>
  </span>
);

const Ico = ({ n, s = 20 }) => (
  <span className="ms-ico" style={{ fontSize: s }} aria-hidden="true">{n}</span>
);

/** Ícone dos botões de link: logo da marca quando houver, senão Material. */
const IcoLink = ({ n, s = 20 }) => {
  if (n === 'google') return <GoogleG size={s} />;
  if (MARCAS[n]) {
    return (
      <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
        <path d={MARCAS[n]} />
      </svg>
    );
  }
  return <Ico n={n} s={s} />;
};

const Estrelas = ({ nota = 5, cor = '#FBBC04' }) => (
  <span style={{ color: cor, letterSpacing: 1 }} aria-label={`${nota} de 5 estrelas`}>
    {'★'.repeat(Math.round(nota))}
  </span>
);

/**
 * Endereco do botao cadastrado no editor. Aceita numero puro nos icones de
 * WhatsApp e telefone (o time digita "11 95293-4176", nao monta o link).
 */
function hrefDoLink(l) {
  const url = String(l.url || '').trim();
  const digitos = url.replace(/\D/g, '');
  const soNumero = /^[\d\s()+.-]+$/.test(url) && digitos.length >= 8;
  if (soNumero && l.icone === 'chat') return `https://wa.me/${digitos.length <= 11 ? '55' + digitos : digitos}`;
  if (soNumero && l.icone === 'call') return `tel:+${digitos.length <= 11 ? '55' + digitos : digitos}`;
  return url;
}

export default function MiniSite({ t, d, preview = false }) {
  // Topo "categoria · cidade": se a categoria (editada à mão) já traz a
  // cidade, não repete ("…em Piracicaba, São Paulo · Piracicaba").
  const sufixoCidade = d.cidade && !String(d.categoria || '').toLowerCase().includes(String(d.cidade).toLowerCase())
    ? ` · ${d.cidade}` : '';
  const foneDigitos = String(d.telefone || '').replace(/\D/g, '');
  const waDigitos = String(d.whatsapp || d.telefone || '').replace(/\D/g, '');
  // Forma/tamanho/enquadramento da logo. Padrão "contain" porque logo
  // horizontal com texto era decapitada pelo recorte circular do template.
  const logoForma = d.logoForma || 'circulo';
  const logoAjuste = d.logoAjuste || 'contain';
  const logoAltura = Number(d.logoTamanho) > 0 ? Number(d.logoTamanho) : 88;
  const logoLargura = logoForma === 'circulo' ? logoAltura
    : logoForma === 'livre' ? Math.round(logoAltura * 2.4) : Math.round(logoAltura * 1.35);
  const logoRaio = logoForma === 'circulo' ? '50%'
    : logoForma === 'quadrado' ? 6 : logoForma === 'livre' ? 0 : 16;

  const wa = waDigitos ? `https://wa.me/${waDigitos.length <= 11 ? '55' + waDigitos : waDigitos}` : null;
  const tel = foneDigitos ? `tel:+55${foneDigitos}` : null;
  // Rota pelas coordenadas do perfil, nao pelo texto do endereco: com endereco
  // escrito por extenso o Google geocodifica por conta e larga o usuario no meio
  // da cidade, longe do comercio. So cai no texto se o perfil nao tiver lat/lng.
  // O place_id (do link de avaliacao) abre a rota no pino da empresa.
  // Mesma regra do site publicado (georanking-minisite).
  const lat = d.latLong?.lat ?? d.latLong?.latitude;
  const lng = d.latLong?.lng ?? d.latLong?.longitude;
  const destino = (lat != null && lng != null) ? `${lat},${lng}` : (d.endereco || null);
  const placeId = d.placeId || (String(d.reviewUrl || '').match(/placeid=([\w-]+)/) || [])[1];
  const rota = destino
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destino)}`
      + (placeId ? `&destination_place_id=${encodeURIComponent(placeId)}` : '')
    : null;
  // Links do editor mandam: com pelo menos um cadastrado, a pagina mostra SO
  // eles (pedido do time em 07/10/2026). Sem nenhum, ficam os botoes
  // automaticos. Mesma regra do site publicado (georanking-minisite).
  const links = (d.links || []).filter((l) => l && l.url);
  const automaticos = links.length === 0;

  const btnBase = {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
    padding: '15px 18px', borderRadius: t.radius, fontWeight: 700, fontSize: 14.5,
    textDecoration: 'none', marginBottom: 11, border: '1px solid transparent',
  };
  const btnSec = { ...btnBase, background: t.card, border: t.cardBorder, color: t.ink };
  const btnPri = {
    ...btnBase, background: t.accent, color: t.onAccent,
    ...(t.sombraBtn ? { boxShadow: `0 8px 20px ${t.accent}44` } : {}),
  };
  const secao = { width: '100%', marginTop: 26, textAlign: 'left' };
  const tituloSecao = {
    fontSize: 12, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase',
    opacity: .55, margin: '0 0 11px',
  };
  const chip = {
    fontSize: 12, padding: '7px 12px', borderRadius: 99, fontWeight: 600,
    background: t.chip, border: t.chipBorder, color: t.chipInk || t.ink,
  };

  const temCapa = (t.capaTopo || t.heroFoto) && d.capa;

  return (
    <div style={{
      position: 'relative', minHeight: '100%', background: t.bg, color: t.ink,
      fontFamily: t.font, overflow: 'hidden',
    }}>
      {t.glow && <div style={{ position: 'absolute', inset: 0, background: t.glow, pointerEvents: 'none' }} />}
      {t.grade && (
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'repeating-linear-gradient(0deg,rgba(47,180,255,.05) 0 1px,transparent 1px 34px),repeating-linear-gradient(90deg,rgba(47,180,255,.05) 0 1px,transparent 1px 34px)',
        }} />
      )}
      {t.listra && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 8,
          background: `repeating-linear-gradient(45deg,${t.accent} 0 14px,${t.bg} 14px 28px)`,
        }} />
      )}
      {t.topoCurvo && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: t.alturaTopo || 190,
          background: t.topoCurvo, borderRadius: '0 0 40px 40px',
        }} />
      )}

      {/* faixa de urgência (modelo 24h) */}
      {t.faixa && (
        <div style={{
          background: t.accent, color: t.onAccent, padding: '16px 20px', fontWeight: 900,
          letterSpacing: '.04em', fontSize: 13, display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 8,
        }}>
          <Ico n="bolt" s={18} />{t.faixa}
        </div>
      )}

      {/* capa / hero com foto */}
      {temCapa && (
        <div style={{
          height: t.heroFoto ? 230 : 132, backgroundImage: `url(${d.capa})`,
          backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative',
        }}>
          {t.heroFoto && (
            <>
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(0,0,0,.15),rgba(0,0,0,.62))' }} />
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: 16, padding: '0 20px', color: '#fff' }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', opacity: .9 }}>
                  {d.categoria}{sufixoCidade}
                </div>
                <div style={{ fontSize: 25, fontWeight: 800, letterSpacing: '-.02em', marginTop: 4, fontFamily: t.headFont }}>{d.nome}</div>
                {d.nota && (
                  <div style={{ marginTop: 6, fontSize: 12.5, fontWeight: 600 }}>
                    <Estrelas nota={d.nota} cor={t.star || '#FBBC04'} /> {d.nota} · {d.totalAvaliacoes} avaliações
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <div style={{
        position: 'relative', padding: t.heroFoto ? '20px 20px 96px' : '30px 20px 96px',
        display: 'flex', flexDirection: 'column',
        alignItems: t.heroFoto ? 'stretch' : 'center',
        textAlign: t.heroFoto ? 'left' : 'center',
        marginTop: t.capaTopo && d.capa ? -46 : 0,
      }}>
        {!t.heroFoto && (
          <>
            {d.logoUrl ? (
              <img src={d.logoUrl} alt={d.nome} style={{
                width: logoLargura, height: logoAltura, borderRadius: logoRaio,
                objectFit: logoAjuste, padding: logoAjuste === 'contain' ? 6 : 0,
                background: '#fff', border: logoForma === 'livre' ? 'none' : t.avatarBorder,
                boxShadow: logoForma === 'livre' ? 'none' : (t.avatarGlow || '0 6px 20px rgba(0,0,0,.14)'),
              }} />
            ) : (
              <div style={{
                width: logoAltura, height: logoAltura, borderRadius: logoRaio, background: t.avatarBg,
                color: t.avatarInk, display: 'grid', placeItems: 'center', fontWeight: 800,
                fontSize: 26, border: t.avatarBorder, boxShadow: t.avatarGlow,
                fontFamily: t.headFont,
              }}>{d.iniciais}</div>
            )}
            <h1 style={{
              fontSize: t.tituloGrande ? 30 : 21, fontWeight: t.pesoTitulo || 800,
              letterSpacing: t.tituloGrande ? '.02em' : '-.02em', margin: '14px 0 3px',
              lineHeight: 1.2, fontFamily: t.headFont,
              color: t.inkTopo || t.ink,   // sobre faixa colorida, texto claro
            }}>{d.nome}</h1>
            <div style={{
              fontSize: 12.5, fontWeight: 600, letterSpacing: '.06em',
              textTransform: 'uppercase', color: t.subTopo || t.accent2 || t.sub,
            }}>{d.categoria}{sufixoCidade}</div>
            {d.nota && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', margin: '10px 0 2px', fontSize: 12.5, fontWeight: 600 }}>
                <Estrelas nota={d.nota} cor={t.star || '#FBBC04'} /> {d.nota} · {d.totalAvaliacoes} avaliações
              </div>
            )}
          </>
        )}

        {d.sobre && (
          <p style={{ fontSize: 13, color: t.sub, margin: '12px 0 0', maxWidth: t.heroFoto ? 'none' : 290, lineHeight: 1.55 }}>
            {d.sobre}
          </p>
        )}

        {/* botões de ação */}
        <div style={{ width: '100%', marginTop: 22 }}>
          {automaticos && t.ligarPrimeiro && tel && (
            <a href={tel} style={{ ...btnPri, fontWeight: 900, fontSize: 16, padding: '19px 18px' }}>
              <Ico n="call" />LIGAR AGORA — 24H
            </a>
          )}
          {automaticos && wa && (
            <a href={wa} style={t.ligarPrimeiro ? { ...btnBase, background: '#25D366', color: '#062b16' } : btnPri}>
              <Ico n="chat" />{d.ctaWhatsapp || 'Falar no WhatsApp'}
            </a>
          )}
          {automaticos && tel && !t.ligarPrimeiro && <a href={tel} style={btnSec}><Ico n="call" />Ligar agora</a>}
          {automaticos && rota && <a href={rota} style={btnSec}><Ico n="near_me" />Como chegar</a>}
          {links.map((l, i) => (
            <a key={i} href={hrefDoLink(l)} style={btnSec}><IcoLink n={iconeDoLink(l)} />{l.texto}</a>
          ))}
          {automaticos && d.reviewUrl && (
            <a href={d.reviewUrl} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
              width: '100%', padding: 15, borderRadius: t.radius, fontWeight: 800,
              fontSize: 14.5, textDecoration: 'none', marginTop: 12,
              background: '#ffffff', color: '#1f2328', border: '1px solid rgba(0,0,0,.08)',
            }}>
              <GoogleG />Avaliar no Google
            </a>
          )}
        </div>

        {Array.isArray(d.servicos) && d.servicos.length > 0 && (
          <div style={secao}>
            <h2 style={tituloSecao}>O que fazemos</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {d.servicos.slice(0, 8).map((s, i) => (
                <span key={i} style={chip}>{typeof s === 'string' ? s : (s.name || s.title)}</span>
              ))}
            </div>
          </div>
        )}

        {Array.isArray(d.fotos) && d.fotos.length > 0 && (
          <div style={secao}>
            <h2 style={tituloSecao}>{d.tituloGaleria || 'Nosso trabalho'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }}>
              {d.fotos.slice(0, 6).map((f, i) => (
                <img key={i} src={typeof f === 'string' ? f : f.url} alt={`${d.nome} — foto ${i + 1}`}
                  loading="lazy"
                  style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 10, display: 'block' }} />
              ))}
            </div>
          </div>
        )}

        {/* Depoimentos do Google NAO sao exibidos: o Google devolve as avaliacoes
            recentes, boas ou ruins, e a pagina do cliente acabava estampando uma
            avaliacao ruim. Decisao do Reinaldo em 24/09/2026. A nota media e o
            botao "Avaliar no Google" continuam. Igual ao site publicado. */}

        {d.horarioTexto && (
          <div style={secao}>
            <h2 style={tituloSecao}>Horário</h2>
            <div style={{ padding: 14, borderRadius: 14, fontSize: 12.5, background: t.card, border: t.cardBorder, whiteSpace: 'pre-line' }}>
              {d.horarioTexto}
            </div>
          </div>
        )}

        <div style={{ marginTop: 26, fontSize: 11, opacity: .55, lineHeight: 1.6, textAlign: t.heroFoto ? 'left' : 'center' }}>
          <strong>{d.nome}</strong><br />
          {d.endereco && <>{d.endereco}<br /></>}
          {d.telefone}
        </div>
      </div>

      {/* barra fixa de conversão (automática; some quando há links do editor) */}
      {automaticos && (wa || tel || rota) && <div style={{
        position: preview ? 'absolute' : 'fixed', left: 0, right: 0, bottom: 0, zIndex: 20,
        display: 'flex', gap: 7, padding: '11px 13px calc(15px + env(safe-area-inset-bottom))',
        background: t.dock, borderTop: t.dockBorder, backdropFilter: 'blur(14px)',
      }}>
        {wa && <a href={wa} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '12px 4px', borderRadius: 13, fontSize: 12, fontWeight: 700, textDecoration: 'none', background: '#25D366', color: '#062b16' }}><Ico n="chat" s={17} />WhatsApp</a>}
        {tel && <a href={tel} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '12px 4px', borderRadius: 13, fontSize: 12, fontWeight: 700, textDecoration: 'none', background: t.accent, color: t.onAccent }}><Ico n="call" s={17} />Ligar</a>}
        {rota && <a href={rota} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '12px 4px', borderRadius: 13, fontSize: 12, fontWeight: 700, textDecoration: 'none', background: t.card, border: t.cardBorder, color: t.ink }}><Ico n="near_me" s={17} />Rota</a>}
      </div>}
    </div>
  );
}
